import type { SitePrivateConfig, PaymentConfigValidation } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { useSiteConfigStore } from '../use-site-config-store';
import { useStationConfigStore } from '../use-station-config-store';

/**
 * Interface representing the state of the payment information store.
 */
interface State extends StateTree {
  isPaymentInfoValid: boolean;
  showPaymentInfo: boolean;
}

/**
 * Creates a Pinia store for managing payment information.
 * This store handles validation and saving of payment information.
 *
 * @param {string} [storeId='paymentInfo'] - The unique ID for the store. Defaults to 'paymentInfo'.
 * @returns {import('pinia').Store} The store instance with state and actions for validating and saving payment info.
 */
export function usePaymentInfoStore(storeId = 'paymentInfo') {
  const store = defineStore(storeId, {
    state: (): State => ({
      isPaymentInfoValid: false,
      showPaymentInfo: false,
    }),
    actions: {
      /**
       * Validates the payment configuration using the provided payload.
       * This function sends the payment configuration data to an API endpoint and updates the validation status.
       *
       * @param {PaymentConfigValidation} payload - The payment configuration to validate.
       * @returns {Promise<void>} A promise that resolves when the validation is complete.
       */
      async validatePaymentInfo(payload: PaymentConfigValidation): Promise<void> {
        const response = await this.cdlApi.post('/payment/configvalidate', payload);
        this.isPaymentInfoValid = response.data?.success;
      },

      /**
       * Saves the payment configuration for the given ID.
       * Depending on the `isSiteConfig` flag, the configuration will be saved either for a site or another entity.
       *
       * @param {string} id - The ID of the entity (site or station) for which the payment configuration is being saved.
       * @param {boolean} isSiteConfig - Flag indicating whether the configuration is for a site (`true`) or for another entity like a station (`false`).
       * @param {Partial<SitePrivateConfig>} payload - A partial object containing the configuration updates.
       * @returns {Promise<void>} A promise that resolves when the payment information is saved.
       */
      async savePaymentInfo(
        id: string,
        isSiteConfig: boolean,
        payload: Partial<SitePrivateConfig>,
      ): Promise<void> {
        // Save private payment info configuration for site
        if (isSiteConfig) {
          // Set site configurations store
          const siteConfigStore = useSiteConfigStore();

          // Save private site configuration
          await Promise.all([siteConfigStore.savePrivate(id, payload)]);
        } else {
          const stationConfigStore = useStationConfigStore();

          // Save private station configuration
          await Promise.all([stationConfigStore.saveStationPrivateConfig(id, payload)]);
        }
      },
    },
    pagination: false,
  });

  return store();
}
